import { useState } from 'react';
import { Building2, Save, Plus, Trash2, LayoutGrid, CheckCircle2, ArrowLeft, Loader2 } from 'lucide-react';
import { cn } from '@/lib/utils';
import { useOrganizationHierarchy, DbOrganization } from '@/lib/useOrganizationHierarchy';
import { useToast } from '@/lib/toast';

export function SettingsPage({ onBack, initialTab = 'profile' }: { onBack: () => void, initialTab?: 'profile' | 'departments' }) {
  const [activeTab, setActiveTab] = useState<'profile' | 'departments'>(initialTab);

  // Hook into Supabase
  const {
    organizations,
    departments,
    loading,
    addOrganization,
    updateOrganization,
    deleteOrganization,

    addDepartment,
    deleteDepartment
  } = useOrganizationHierarchy();
  const toast = useToast();

  const [editingOrgId, setEditingOrgId] = useState<string | null>(null);
  const [isAddingOrg, setIsAddingOrg] = useState(false);
  const [savedProfile, setSavedProfile] = useState(false);
  const [isSaving, setIsSaving] = useState(false);


  const [newDeptName, setNewDeptName] = useState('');
  const [newDeptOrgId, setNewDeptOrgId] = useState('');

  const [orgForm, setOrgForm] = useState<Partial<DbOrganization>>({});

  const handleEditOrg = (org: DbOrganization) => {
    setOrgForm(org);
    setEditingOrgId(org.id);
    setIsAddingOrg(false);
  };

  const handleAddOrg = () => {
    setOrgForm({ organization_name: '', verified_domain: '' });
    setEditingOrgId(null);
    setIsAddingOrg(true);
  };

  const handleDeleteOrg = async (id: string) => {
    if (!window.confirm("Are you sure you want to delete this organization? This will also permanently remove all associated users and departments from the database!")) return;
    try {
      await deleteOrganization(id);
      toast.push("Organization successfully deleted.");
    } catch (e: any) {
      console.error(e);
      toast.push("Failed to delete organization: " + (e.response?.data?.message || e.message));
    }
  };

  const handleSaveProfile = async () => {
    setIsSaving(true);
    try {
      if (isAddingOrg && orgForm.organization_name) {
        const newOrg = await addOrganization(orgForm);
        if (newOrg) {
          setEditingOrgId(newOrg.id);
          setIsAddingOrg(false);
          setOrgForm(newOrg);
        }
      } else if (editingOrgId && orgForm.organization_name) {
        await updateOrganization(editingOrgId, orgForm);
      }
      setSavedProfile(true);
      setTimeout(() => {
        setSavedProfile(false);
      }, 1500);
    } catch (e) {
      console.error(e);
    } finally {
      setIsSaving(false);
    }
  };


  const cancelEdit = () => {
    setEditingOrgId(null);
    setIsAddingOrg(false);
  };

  const handleCreateDepartment = async () => {
    if (!newDeptName.trim() || !newDeptOrgId) return;
    try {
      setIsSaving(true);
      await addDepartment(newDeptOrgId, newDeptName);
      setNewDeptName('');
    } catch (e) {
      console.error(e);
    } finally {
      setIsSaving(false);
    }
  };

  const removeDepartment = async (id: string) => {
    try {
      await deleteDepartment(id);
    } catch (e) {
      console.error(e);
    }
  };

  if (loading && organizations.length === 0) {
    return (
      <div className="flex h-full items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="flex h-full flex-col">
      {/* settings header, styled like team header */}
      <div className="flex items-center gap-3 border-b border-border bg-card/60 px-6 py-4">
        <button
          onClick={onBack}
          className="flex h-8 w-8 items-center justify-center rounded-lg border border-border text-muted-foreground transition hover:bg-muted hover:text-foreground mr-2"
        >
          <ArrowLeft className="h-4 w-4" />
        </button>
        <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-primary/10 text-primary">
          <Building2 className="h-5 w-5" />
        </div>
        <div>
          <h1 className="font-display text-lg font-semibold tracking-tight">Organization Settings</h1>
          <div className="flex items-center gap-2 text-xs text-muted-foreground">
            Configure your core business profile and structural departments.
          </div>
        </div>
      </div>

      <div className="flex-1 overflow-y-auto bg-muted/10 p-6">
        <div className="mx-auto max-w-5xl space-y-6">
          
          {/* Navigation Tabs - Dashboard Style */}
          <div className="flex items-center gap-2 border-b border-border pb-2">
            <button
              onClick={() => setActiveTab('profile')}
              className={cn(
                "flex items-center gap-2 rounded-lg px-4 py-2 text-sm font-medium transition",
                activeTab === 'profile' ? "bg-card shadow-sm border border-border text-foreground" : "text-muted-foreground hover:bg-muted"
              )}
            >
              <Building2 className="h-4 w-4" />
              Organizations
            </button>
            <button
              onClick={() => setActiveTab('departments')}
              className={cn(
                "flex items-center gap-2 rounded-lg px-4 py-2 text-sm font-medium transition",
                activeTab === 'departments' ? "bg-card shadow-sm border border-border text-foreground" : "text-muted-foreground hover:bg-muted"
              )}
            >
              <LayoutGrid className="h-4 w-4" />
              Departments
            </button>
          </div>

          {activeTab === 'profile' && !editingOrgId && !isAddingOrg && (
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <h2 className="font-display text-lg font-medium">Organizations</h2>
                <button
                  onClick={handleAddOrg}
                  className="inline-flex items-center gap-2 rounded-lg bg-primary px-4 py-2 text-sm font-medium text-primary-foreground transition hover:opacity-90"
                >
                  <Plus className="h-4 w-4" /> Add Organization
                </button>
              </div>
              <div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-3">
                {organizations.map(org => (
                  <div key={org.id} className="relative rounded-xl border border-border bg-card p-5 shadow-sm transition hover:shadow-md">
                    <div className="flex items-start justify-between">
                      <div className="flex items-center gap-3">
                        <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-primary/10 text-primary">
                          <Building2 className="h-5 w-5" />
                        </div>
                        <div>
                          <div className="font-display font-medium text-foreground">{org.organization_name}</div>
                          <div className="text-xs text-muted-foreground">{org.verified_domain || 'No Domain'}</div>
                        </div>
                      </div>
                    </div>
                    <div className="mt-4 flex gap-2 pt-4 border-t border-border">
                      <button onClick={() => handleEditOrg(org)} className="text-xs font-medium text-primary hover:underline">Edit</button>
                      <button onClick={() => handleDeleteOrg(org.id)} className="text-xs font-medium text-rose-500 hover:underline">Delete</button>
                    </div>
                  </div>
                ))}
                {organizations.length === 0 && (
                  <div className="col-span-full py-8 text-center text-sm text-muted-foreground bg-card border border-dashed border-border rounded-xl">
                    No organizations found. Create one to get started.
                  </div>
                )}
              </div>
            </div>
          )}

          {activeTab === 'profile' && (editingOrgId || isAddingOrg) && (
            <div className="space-y-6">
              <div className="flex items-center justify-between">
                <div>
                  <button onClick={cancelEdit} className="flex items-center gap-2 text-xs font-medium text-muted-foreground hover:text-foreground mb-4">
                    <ArrowLeft className="h-4 w-4" /> Back to Organizations
                  </button>
                  <h2 className="font-display text-lg font-medium">Organization Profile</h2>
                  <p className="text-xs text-muted-foreground mt-0.5">Basic details about this organization.</p>
                </div>
              </div>

              <div className="rounded-xl border border-border bg-card p-6 shadow-sm max-w-2xl">
                <div className="space-y-5">
                  <div className="grid grid-cols-2 gap-5">
                    <div className="col-span-2 sm:col-span-1">
                      <label className="mb-1.5 block text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">Organization Name *</label>
                      <input 
                        value={orgForm.organization_name || ''} 
                        onChange={e => setOrgForm({...orgForm, organization_name: e.target.value})}
                        className="w-full rounded-lg border border-input bg-background px-3 py-2 text-sm outline-none transition focus:border-primary" 
                        placeholder="e.g. Acme Corp"
                      />
                    </div>
                    <div className="col-span-2 sm:col-span-1">
                      <label className="mb-1.5 block text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">GSTIN</label>
                      <input 
                        value={orgForm.gstin || ''} 
                        onChange={e => setOrgForm({...orgForm, gstin: e.target.value})}
                        className="w-full rounded-lg border border-input bg-background px-3 py-2 text-sm outline-none transition focus:border-primary" 
                        placeholder="22AAAAA0000A1Z5"
                      />
                    </div>
                    <div className="col-span-2 sm:col-span-1">
                      <label className="mb-1.5 block text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">Contact</label>
                      <input 
                        value={orgForm.contact || ''} 
                        onChange={e => setOrgForm({...orgForm, contact: e.target.value})}
                        className="w-full rounded-lg border border-input bg-background px-3 py-2 text-sm outline-none transition focus:border-primary" 
                        placeholder="Phone or Email"
                      />
                    </div>
                    <div className="col-span-2">
                      <label className="mb-1.5 block text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">Address</label>
                      <textarea 
                        value={orgForm.address || ''} 
                        onChange={e => setOrgForm({...orgForm, address: e.target.value})}
                        className="w-full rounded-lg border border-input bg-background px-3 py-2 text-sm outline-none transition focus:border-primary resize-none h-20" 
                        placeholder="Full business address..."
                      />
                    </div>
                  </div>

                  <div className="flex items-center gap-3 pt-6 border-t border-border mt-6">
                    <button 
                      onClick={handleSaveProfile}
                      disabled={isSaving || !orgForm.organization_name}
                      className="inline-flex items-center gap-2 rounded-lg bg-primary px-4 py-2 text-sm font-medium text-primary-foreground transition hover:opacity-90 disabled:opacity-50"
                    >
                      {isSaving ? <Loader2 className="h-4 w-4 animate-spin" /> : savedProfile ? <CheckCircle2 className="h-4 w-4" /> : <Save className="h-4 w-4" />}
                      {savedProfile ? 'Saved Successfully' : isSaving ? (isAddingOrg ? 'Creating...' : 'Updating...') : (isAddingOrg ? 'Create Organization' : 'Update Organization')}
                    </button>
                    <button onClick={cancelEdit} className="rounded-lg px-4 py-2 text-sm font-medium text-muted-foreground hover:bg-muted transition">
                      Cancel
                    </button>
                  </div>
                </div>
              </div>
            </div>
          )}

          {activeTab === 'departments' && (
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
              <div className="lg:col-span-2 space-y-4">
                <div className="flex items-center justify-between">
                  <div>
                    <h2 className="font-display text-lg font-medium">Departments</h2>
                    <p className="text-xs text-muted-foreground mt-0.5">Manage structural units within your organizations.</p>
                  </div>
                </div>
                
                <div className="space-y-2">
                  {departments.map((dept) => {
                    const org = organizations.find(o => o.id === dept.organization_id);
                    return (
                      <div key={dept.id} className="flex items-center justify-between rounded-xl border border-border bg-card p-4 shadow-sm">
                        <div className="flex items-center gap-4">
                          <div className={`flex h-10 w-10 items-center justify-center rounded-lg bg-blue-500/10 text-blue-500`}>
                            <LayoutGrid className="h-5 w-5" />
                          </div>
                          <div>
                            <div className="font-display font-medium text-foreground">{dept.name}</div>
                            <div className="text-xs text-muted-foreground flex items-center gap-2">
                              <span>Org: {org?.organization_name || 'Unknown'}</span>
                            </div>
                          </div>
                        </div>
                        <div className="flex items-center gap-3">
                          <button onClick={() => removeDepartment(dept.id)} className="flex h-8 w-8 items-center justify-center rounded-lg text-muted-foreground hover:bg-rose-500/10 hover:text-rose-500 transition">
                            <Trash2 className="h-4 w-4" />
                          </button>
                        </div>
                      </div>
                    );
                  })}
                  {departments.length === 0 && (
                    <div className="py-8 text-center text-sm text-muted-foreground bg-card border border-dashed border-border rounded-xl">
                      No departments found.
                    </div>
                  )}
                </div>
              </div>

              <div className="lg:col-span-1">
                <div className="rounded-xl border border-border bg-card p-5 shadow-sm sticky top-6">
                  <h3 className="font-display font-medium mb-4 flex items-center gap-2"><Plus className="h-4 w-4 text-primary" /> Add Department</h3>
                  <div className="space-y-4">
                    <div>
                      <label className="mb-1.5 block text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">Organization</label>
                      <select 
                        value={newDeptOrgId}
                        onChange={e => setNewDeptOrgId(e.target.value)}
                        className="w-full rounded-lg border border-input bg-background px-3 py-2 text-sm outline-none transition focus:border-primary"
                      >
                        <option value="">Select Organization...</option>
                        {organizations.map(o => (
                          <option key={o.id} value={o.id}>{o.organization_name}</option>
                        ))}
                      </select>
                    </div>
                    <div>
                      <label className="mb-1.5 block text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">Department Name</label>
                      <input 
                        value={newDeptName}
                        onChange={(e) => setNewDeptName(e.target.value)}
                        placeholder="e.g. Engineering"
                        className="w-full rounded-lg border border-input bg-background px-3 py-2 text-sm outline-none transition focus:border-primary"
                      />
                    </div>
                    <button 
                      onClick={handleCreateDepartment}
                      disabled={isSaving || !newDeptName || !newDeptOrgId}
                      className="w-full inline-flex justify-center items-center gap-2 rounded-lg bg-primary px-4 py-2 text-sm font-medium text-primary-foreground transition hover:opacity-90 disabled:opacity-50"
                    >
                      {isSaving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Plus className="h-4 w-4" />} Create Department
                    </button>
                  </div>
                </div>
              </div>
            </div>
          )}

        </div>
      </div>
    </div>
  );
}
